import React, { useState, useEffect } from 'react';
import { CrossIcon, AppleIcon, WindowsIcon, AndroidIcon, LinuxIcon, DisplayIcon } from '../../icons';
import { fetchSessions, revokeSession } from '../../../services/auth';
import toast from 'react-hot-toast';

const getDeviceIcon = (device) => {
  if (device === 'apple')   return <AppleIcon   size={18} className="text-[#121212] dark:text-[#D0D0D0]" />;
  if (device === 'windows') return <WindowsIcon  size={18} color="#1DA4F3" />;
  if (device === 'android') return <AndroidIcon  size={18} color="#86B93E" />;
  if (device === 'linux')   return <LinuxIcon    size={18} className="text-[#7B7B7B] dark:text-[#848484]" />;
  return <DisplayIcon size={18} className="text-[#7B7B7B] dark:text-[#848484]" />;
};

const SessionItem = ({ browser, isCurrent, location, lastActivity, osIcon, onTerminate }) => (
  <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#ECECEC] dark:border-[#202020] last:border-b-0">
    <div className="flex items-center gap-3 min-w-0">
      {/* Device icon */}
      <div className="w-8 h-8 rounded-lg bg-[#F7F7F7] dark:bg-[#161616] flex items-center justify-center shrink-0">
        {osIcon}
      </div>

      {/* Text */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#121212] dark:text-[#D0D0D0] truncate">
            {browser}
          </span>
          {isCurrent && (
            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F7F7F7] dark:bg-[#252525] text-[#4ABA62]">
              Current
            </span>
          )}
        </div>
        <p className="text-[11px] text-[#7B7B7B] dark:text-[#848484] mt-0.5 truncate">
          {[location, lastActivity].filter(Boolean).join(' · ')}
        </p>
      </div>
    </div>

    {/* Terminate button */}
    {!isCurrent && (
      <button
        onClick={onTerminate}
        className="ml-4 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center opacity-50 hover:opacity-100 hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-all duration-200"
        aria-label="Terminate session"
      >
        <CrossIcon size={14} className="text-[#7B7B7B] dark:text-[#848484]" />
      </button>
    )}
  </div>
);

const SessionsView = () => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      setSessions(await fetchSessions());
    } catch {
      toast.error('Failed to fetch sessions');
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTerminate = async (sessionId) => {
    if (!window.confirm('Revoke this session? The device will be logged out immediately.')) return;
    try {
      await revokeSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session revoked');
    } catch {
      toast.error('Failed to revoke session');
    }
  };

  const formatLastActivity = (usedAt) => {
    if (!usedAt) return null;
    const date = new Date(usedAt);
    const now = new Date();
    const days = Math.floor((now - date) / 86400000);
    const weeks = Math.floor(days / 7);
    if (days === 0) return 'Active today';
    if (days === 1) return 'Active yesterday';
    if (days < 7)  return `Active ${days} days ago`;
    if (weeks < 4) return `Active ${weeks} week${weeks > 1 ? 's' : ''} ago`;
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  return (
    <div className="bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-2xl overflow-hidden mb-4">
      {/* Card header */}
      <div className="px-5 py-4 border-b border-[#ECECEC] dark:border-[#202020]">
        <h2 className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0] tracking-[-0.1px]">
          Active sessions
        </h2>
        <p className="mt-0.5 text-xs text-[#7B7B7B] dark:text-[#848484]">
          Devices currently signed in to your account.
        </p>
      </div>

      {/* Session list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#121212] dark:border-[#D0D0D0]" />
        </div>
      ) : sessions.length === 0 ? (
        <p className="px-5 py-10 text-xs text-center text-[#7B7B7B] dark:text-[#848484]">
          No active sessions found.
        </p>
      ) : (
        sessions.map((session) => (
          <SessionItem
            key={session.id}
            browser={session.browser || 'Unknown Browser'}
            isCurrent={session.isCurrent}
            location={session.userCountry || 'Unknown location'}
            lastActivity={session.isCurrent ? null : formatLastActivity(session.usedAt)}
            osIcon={getDeviceIcon(session.device)}
            onTerminate={() => handleTerminate(session.id)}
          />
        ))
      )}
    </div>
  );
};

export default SessionsView;
